"use strict";

/**
  * @reach/menu-button v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-menu-button.tsx
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.array.push.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.define-properties.js";
import "core-js/modules/es.object.define-property.js";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["as", "id", "children"],
  _excluded2 = ["as"],
  _excluded3 = ["as"],
  _excluded4 = ["as"],
  _excluded5 = ["as"],
  _excluded6 = ["as", "component", "onSelect"],
  _excluded7 = ["portal"],
  _excluded8 = ["as"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { Popover } from "@reach/popover";
import { DropdownProvider, useDropdownItem, useDropdownItems, useDropdownPopover, useDropdownTrigger, useDropdownContext } from "@reach/dropdown";
import { noop } from "@reach/utils";
import { isFragment } from "react-is";
var Menu = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var _ref$as = _ref.as,
    Comp = _ref$as === void 0 ? React.Fragment : _ref$as,
    id = _ref.id,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var parentIsFragment = React.useMemo(function () {
    try {
      return isFragment( /* @__PURE__ */React.createElement(Comp, null));
    } catch (err) {
      return false;
    }
  }, [Comp]);
  var props = parentIsFragment ? {} : _objectSpread({
    ref: forwardedRef,
    id: id,
    "data-reach-menu": ""
  }, rest);
  return /* @__PURE__ */React.createElement(Comp, _objectSpread({}, props), /* @__PURE__ */React.createElement(DropdownProvider, {
    id: id,
    children: children
  }));
});
Menu.displayName = "Menu";
var MenuButton = /*#__PURE__*/React.forwardRef(function (_ref2, forwardedRef) {
  var _ref2$as = _ref2.as,
    Comp = _ref2$as === void 0 ? "button" : _ref2$as,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var _useDropdownTrigger = useDropdownTrigger(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    _useDropdownTrigger$d = _useDropdownTrigger.data,
    isExpanded = _useDropdownTrigger$d.isExpanded,
    controls = _useDropdownTrigger$d.controls,
    props = _useDropdownTrigger.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-expanded": isExpanded ? true : void 0,
    "aria-haspopup": true,
    "aria-controls": controls
  }, props), {}, {
    "data-reach-menu-button": ""
  }));
});
MenuButton.displayName = "MenuButton";
var MenuItemImpl = /*#__PURE__*/React.forwardRef(function (_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "div" : _ref3$as,
    rest = _objectWithoutProperties(_ref3, _excluded3);
  var _useDropdownItem = useDropdownItem(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    disabled = _useDropdownItem.data.disabled,
    props = _useDropdownItem.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    role: "menuitem"
  }, props), {}, {
    "aria-disabled": disabled || void 0,
    "data-reach-menu-item": ""
  }));
});
MenuItemImpl.displayName = "MenuItemImpl";
var MenuItem = /*#__PURE__*/React.forwardRef(function (_ref4, forwardedRef) {
  var _ref4$as = _ref4.as,
    as = _ref4$as === void 0 ? "div" : _ref4$as,
    props = _objectWithoutProperties(_ref4, _excluded4);
  return /* @__PURE__ */React.createElement(MenuItemImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    as: as
  }));
});
MenuItem.displayName = "MenuItem";
var MenuItems = /*#__PURE__*/React.forwardRef(function (_ref5, forwardedRef) {
  var _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    rest = _objectWithoutProperties(_ref5, _excluded5);
  var _useDropdownItems = useDropdownItems(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    _useDropdownItems$dat = _useDropdownItems.data,
    activeDescendant = _useDropdownItems$dat.activeDescendant,
    triggerId = _useDropdownItems$dat.triggerId,
    props = _useDropdownItems.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-activedescendant": activeDescendant,
    "aria-labelledby": triggerId || void 0,
    role: "menu"
  }, props), {}, {
    "data-reach-menu-items": ""
  }));
});
MenuItems.displayName = "MenuItems";
var MenuLink = /*#__PURE__*/React.forwardRef(function (_ref6, forwardedRef) {
  var _ref6$as = _ref6.as,
    as = _ref6$as === void 0 ? "a" : _ref6$as,
    component = _ref6.component,
    onSelect = _ref6.onSelect,
    props = _objectWithoutProperties(_ref6, _excluded6);
  if (true) {
    React.useEffect(function () {
      if (component) {
        console.warn("[@reach/menu-button]: Please use the `as` prop instead of `component`");
      }
    }, [component]);
  }
  return /* @__PURE__ */React.createElement(MenuItemImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-menu-link": "",
    as: as,
    isLink: true,
    onSelect: onSelect || noop
  }));
});
MenuLink.displayName = "MenuLink";
var MenuList = /*#__PURE__*/React.forwardRef(function (_ref7, forwardedRef) {
  var _ref7$portal = _ref7.portal,
    portal = _ref7$portal === void 0 ? true : _ref7$portal,
    props = _objectWithoutProperties(_ref7, _excluded7);
  return /* @__PURE__ */React.createElement(MenuPopover, {
    portal: portal
  }, /* @__PURE__ */React.createElement(MenuItems, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-menu-list": ""
  })));
});
MenuList.displayName = "MenuList";
var MenuPopover = /*#__PURE__*/React.forwardRef(function (_ref8, forwardedRef) {
  var _ref8$as = _ref8.as,
    Comp = _ref8$as === void 0 ? "div" : _ref8$as,
    rest = _objectWithoutProperties(_ref8, _excluded8);
  var _useDropdownPopover = useDropdownPopover(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    _useDropdownPopover$d = _useDropdownPopover.data,
    portal = _useDropdownPopover$d.portal,
    targetRef = _useDropdownPopover$d.targetRef,
    position = _useDropdownPopover$d.position,
    props = _useDropdownPopover.props;
  var sharedProps = {
    "data-reach-menu-popover": ""
  };
  return portal ? /* @__PURE__ */React.createElement(Popover, _objectSpread(_objectSpread(_objectSpread({}, props), sharedProps), {}, {
    as: Comp,
    targetRef: targetRef,
    position: position,
    unstable_skipInitialPortalRender: true
  })) : /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), sharedProps));
});
MenuPopover.displayName = "MenuPopover";
function useMenuButtonContext() {
  var _useDropdownContext = useDropdownContext("useMenuButtonContext"),
    isExpanded = _useDropdownContext.state.isExpanded;
  return React.useMemo(function () {
    return {
      isExpanded: isExpanded
    };
  }, [isExpanded]);
}
export { Menu, MenuButton, MenuItem, MenuItems, MenuLink, MenuList, MenuPopover, useMenuButtonContext };